import React from 'react';
import { addDays, addWeeks, addMonths, addYears, isAfter } from 'date-fns';
import { useSettings } from '@wix/yoshi-flow-editor/tpa-settings/react';
import classNames from 'classnames';
import { useExperiments, useTranslation } from '@wix/yoshi-flow-editor';
import { TPAComponentsConsumer } from 'wix-ui-tpa/TPAComponentsConfig';
import { TextButton } from 'wix-ui-tpa/TextButton';
import { Tooltip } from 'wix-ui-tpa/Tooltip';
import { Spinner } from 'wix-ui-tpa/Spinner';
import { getStatusTextTranslationKey, StatusBadge } from './StatusBadge';
import {
  SubscriptionFrequency,
  SubscriptionStatus,
  SubscriptionTrialPeriod,
  SubscriptionType,
  V1Subscription,
} from '@wix/ambassador-subscriptions-api/types';
import { Balance, BenefitType } from '@wix/ambassador-pricing-plan-benefits-server/types';
import { Address, Subscription } from '@wix/ambassador-wix-ecommerce-subscriptions/types';
import { connect } from '../../../common/components/runtime-context';
import { ArrowUpIcon } from './ArrowUpIcon';
import { ArrowDownIcon } from './ArrowDownIcon';
import { TextSubscriptionName } from './TextSubscriptionName';
import {
  getDetails,
  getRegionalSettings,
  isDetailsLoading as isDetailsLoadingSelector,
  isDetailsOpen as isDetailsOpenSelector,
} from '../selectors';
import { TextSubscriptionInfo } from './TextSubscriptionInfo';
import { st, classes } from './SubscriptionsListItem.st.css';
import { PAID_PLANS_APP_DEF_ID, STORES_APP_DEF_ID } from '../constants';
import settingsParams from '../settingsParams';
import { RootState } from '../state';

const FrequencyTranslations = {
  [SubscriptionFrequency.DAY]: 'app.details.price-per-day',
  [SubscriptionFrequency.WEEK]: 'app.details.price-per-week',
  [SubscriptionFrequency.MONTH]: 'app.details.price-per-month',
  [SubscriptionFrequency.YEAR]: 'app.details.price-per-year',
  [SubscriptionFrequency.UNDEFINED]: 'app.details.price-undefined',
};

const buildTrialEndDate = (date: Date, trial: SubscriptionTrialPeriod) => {
  const fnMap = {
    [SubscriptionFrequency.DAY]: addDays,
    [SubscriptionFrequency.WEEK]: addWeeks,
    [SubscriptionFrequency.MONTH]: addMonths,
    [SubscriptionFrequency.YEAR]: addYears,
    [SubscriptionFrequency.UNDEFINED]: (_date: Date) => _date,
  };

  return fnMap[trial.unit!](date, trial.period!);
};

type RuntimeProps = ReturnType<typeof mapRuntimeToProps>;

interface Props {
  subscription: V1Subscription;
}

const SubscriptionsListItem = ({
  subscription,
  isDetailsOpen,
  isDetailsLoading,
  openDetails,
  closeDetails,
  details,
  language,
  navigateToPricingPlans,
  navigateToStores,
  cancelSubscription,
}: Props & RuntimeProps) => {
  const { t } = useTranslation();
  const settings = useSettings();

  const hasEndDate = Boolean(subscription.validUntil);
  const validFrom = subscription.validFrom as Date;
  const validUntil = subscription.validUntil as Date;

  const formatDate = (date: string | Date) => {
    if (typeof date === 'string') {
      date = new Date(date);
    }
    return date.toLocaleDateString(language, {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  };

  const formatMoney = (amount: number | string, currency: string) => {
    if (typeof amount === 'string') {
      amount = parseFloat(amount);
    }
    return amount.toLocaleString(language, { style: 'currency', currency });
  };
  const isPPSubscription = subscription.wixAppId === PAID_PLANS_APP_DEF_ID;
  const isStoresSubscription = subscription.wixAppId === STORES_APP_DEF_ID;

  const navigateToVertical = () => {
    if (isPPSubscription) {
      return navigateToPricingPlans();
    }

    if (isStoresSubscription) {
      return navigateToStores();
    }
  };

  let trialEndDate: Date;
  let inTrial = false;

  if (subscription.billingProfile?.trial && subscription.validFrom) {
    trialEndDate = buildTrialEndDate(subscription.validFrom, subscription.billingProfile.trial);
    inTrial = subscription.subscriptionStatus === SubscriptionStatus.ACTIVE && isAfter(trialEndDate, new Date());
  }

  const getFieldA = () => {
    const lines: string[] = [];
    if (subscription.description && subscription.wixAppId === STORES_APP_DEF_ID) {
      lines.push(subscription.description);
    }
    if (subscription.name) {
      lines.push(subscription.name);
    }
    return lines;
  };

  const getFieldB = () => {
    const lines: string[] = [];
    if (subscription.price) {
      const { amount, currency } = subscription.price;
      if (amount && currency) {
        lines.push(
          subscription.recurring && subscription.billingProfile?.frequency
            ? t(FrequencyTranslations[subscription.billingProfile.frequency], { price: formatMoney(amount, currency) })
            : formatMoney(amount, currency),
        );
      }
    } else {
      lines.push(t('app.details.free-plan'));
    }
    if (subscription.subscriptionType === SubscriptionType.ONLINE) {
      if (details?.paymentSubscriptionInfo) {
        const {
          totalBillingCycles,
          totalSuccessfulPayments = 0,
          lastSuccessfullyChargedDate,
          nextChargeAttemptDate,
          paymentMethod,
        } = details.paymentSubscriptionInfo;

        if (hasEndDate && totalBillingCycles) {
          lines.push(t('app.details.payments-completed-of-total', { totalBillingCycles, totalSuccessfulPayments }));
        } else {
          lines.push(t('app.details.payments-completed', { totalSuccessfulPayments }));
        }
        if (lastSuccessfullyChargedDate) {
          lines.push(t('app.details.last-payment', { date: formatDate(lastSuccessfullyChargedDate) }));
        }
        if (nextChargeAttemptDate) {
          lines.push(t('app.details.next-payment', { date: formatDate(nextChargeAttemptDate) }));
        }
        if (paymentMethod === 'creditCard') {
          lines.push(t('app.details.payment-method-credit-card'));
        }
        if (paymentMethod === 'payPal') {
          lines.push(t('app.details.payment-method-paypal'));
        }
      }
    }
    if (subscription.subscriptionType === SubscriptionType.OFFLINE) {
      lines.push(t('app.details.payment-method-offline'));
    }
    return lines;
  };

  const renderFieldC = ({ buyNewPlanText, cancelSubscriptionText }: any) => {
    const hasBuyNewPlanStatus =
      subscription.subscriptionStatus &&
      [SubscriptionStatus.EXPIRED, SubscriptionStatus.PENDING_CANCELLATION, SubscriptionStatus.CANCELED].indexOf(
        subscription.subscriptionStatus,
      ) > -1;

    const hasCancelPlanStatus =
      subscription.subscriptionStatus &&
      [SubscriptionStatus.ACTIVE, SubscriptionStatus.PENDING].indexOf(subscription.subscriptionStatus) > -1;

    let cta: React.ReactElement | undefined;
    if ((isPPSubscription || isStoresSubscription) && hasBuyNewPlanStatus) {
      cta = (
        <TextButton className={classes.cta} onClick={navigateToVertical}>
          {buyNewPlanText}
        </TextButton>
      );
    }
    if (hasCancelPlanStatus && subscription.policy?.cancellableByMember) {
      cta = (
        <TextButton className={classes.cta} onClick={cancelSubscription}>
          {cancelSubscriptionText}
        </TextButton>
      );
    }

    if (cta) {
      return <div className={classes.ctaContainer}>{cta}</div>;
    }
    return null;
  };

  const getFieldD = (): string | null => {
    switch (subscription.subscriptionStatus) {
      case SubscriptionStatus.PENDING:
        return t('app.title.starts', { date: formatDate(validFrom) });
      case SubscriptionStatus.ACTIVE:
        return hasEndDate
          ? t('app.title.expires', { date: formatDate(validUntil) })
          : t('app.title.valid-until-canceled');
      case SubscriptionStatus.PENDING_CANCELLATION:
        return t('app.title.expires', { date: formatDate(validUntil) });
      case SubscriptionStatus.SUSPENDED:
        return null;
      case SubscriptionStatus.EXPIRED:
        return t('app.title.expired', { date: formatDate(validUntil) });
      case SubscriptionStatus.CANCELED:
        return t('app.title.canceled', { date: formatDate(validUntil) });
      default:
        return null;
    }
  };

  const getFieldE = (): string => {
    if (subscription.subscriptionStatus === SubscriptionStatus.PENDING) {
      return hasEndDate
        ? t('app.details.expires', { date: formatDate(validUntil) })
        : t('app.details.valid-until-canceled');
    }
    return t('app.details.start-date', { date: formatDate(validFrom) });
  };

  const getFieldF = (): string | null =>
    inTrial ? t('app.details.trial-ends', { date: formatDate(trialEndDate) }) : null;

  const getFieldG = () => {
    const lines = [];
    if (details?.benefitBalanceItems) {
      const { benefit } = details.benefitBalanceItems[0] as Balance;
      if (benefit?.benefitType === BenefitType.LIMITED) {
        const { remainingCredits, totalCredits } = benefit;
        lines.push(t('app.details.sessions-remaining-without-total', { remainingCredits, totalCredits }));
      }
    }
    if (details?.storesSubscription) {
      const { lineItems, shippingInfo } = details.storesSubscription as Subscription;
      lines.push(t('app.details.quantity', { quantity: lineItems?.[0].quantity }));
      if (shippingInfo?.shipmentDetails?.address) {
        const { address } = shippingInfo.shipmentDetails;
        const addressLines = ['addressLine1', 'addressLine2', 'city', 'zipCode', 'country']
          .map((field) => address[field as keyof Address])
          .filter(Boolean);

        if (addressLines.length) {
          lines.push(t('app.details.shipping-to'));
          addressLines.forEach((line) => lines.push(line));
        }
      }
    }
    return lines;
  };

  const fieldA = getFieldA();
  const fieldB = getFieldB();
  const fieldD = getFieldD();
  const fieldE = getFieldE();
  const fieldF = getFieldF();
  const fieldG = getFieldG();

  return (
    <TPAComponentsConsumer>
      {({ mobile }) => (
        <div className={st(classes.root, mobile ? { mobile } : undefined)} data-hook="subscription-list-item">
          <div
            id={`subscription-title-${subscription.id}`}
            className={classes.titleContainer}
            data-hook="subscription-list-item-title"
            onClick={() => (isDetailsOpen ? closeDetails() : openDetails())}
          >
            <div className={classes.lft}>
              <div className={classNames(classes.title, classes.columnA)}>
                {fieldA.map((line, i) => (
                  <div key={i}>
                    <TextSubscriptionName>{line}</TextSubscriptionName>
                  </div>
                ))}
              </div>
              {fieldD && (
                <div className={classes.columnB}>
                  <TextSubscriptionName>{fieldD}</TextSubscriptionName>
                </div>
              )}
              {mobile && (
                <div className={classes.statusBadgeContainer}>
                  <StatusBadge subscription={subscription} />
                </div>
              )}
            </div>
            <div className={classes.rgt}>
              {!mobile && (
                <div className={classes.tooltipWrapper}>
                  <Tooltip content={t(getStatusTextTranslationKey(subscription))} placement="top">
                    <div className={classes.statusBadgeContainer}>
                      <StatusBadge subscription={subscription} />
                    </div>
                  </Tooltip>
                </div>
              )}
              <div className={classes.iconContainer}>
                <button
                  style={{ fontSize: 0 }}
                  aria-expanded={isDetailsOpen ? 'true' : 'false'}
                  aria-label="more details"
                  aria-describedby={`subscription-title-${subscription.id}`}
                >
                  {isDetailsOpen ? (
                    <ArrowUpIcon className={classes.arrow} />
                  ) : (
                    <ArrowDownIcon className={classes.arrow} />
                  )}
                </button>
              </div>
            </div>
          </div>

          {isDetailsOpen && (
            <div className={classes.details} data-hook="subscription-list-item-details">
              <div className={classes.contentContainer}>
                {isDetailsLoading && !details && (
                  <div className={classes.loader}>
                    <Spinner />
                  </div>
                )}
                {details && (
                  <>
                    <div className={classes.columnA}>
                      {fieldB.map((line, i) => (
                        <div key={i}>
                          <TextSubscriptionInfo>{line}</TextSubscriptionInfo>
                        </div>
                      ))}
                    </div>
                    <div className={classes.columnB}>
                      <div>
                        <TextSubscriptionInfo>{fieldE}</TextSubscriptionInfo>
                      </div>
                      {fieldF && (
                        <div>
                          <TextSubscriptionInfo>{fieldF}</TextSubscriptionInfo>
                        </div>
                      )}
                      {fieldG.map((line, i) => (
                        <div key={i}>
                          <TextSubscriptionInfo>{line}</TextSubscriptionInfo>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
              {details &&
                renderFieldC({
                  buyNewPlanText: settings.get(settingsParams.buyNewPlanText),
                  cancelSubscriptionText: settings.get(settingsParams.cancelSubscriptionText),
                })}
            </div>
          )}
        </div>
      )}
    </TPAComponentsConsumer>
  );
};

const mapRuntimeToProps = (
  state: RootState,
  { subscription }: Props,
  { openDetails, closeDetails, navigateToPricingPlans, navigateToStores, openCancelConfirmModal }: any,
) => {
  const subscriptionId: string = subscription.id!;
  return {
    isDetailsOpen: isDetailsOpenSelector(state, subscriptionId),
    isDetailsLoading: isDetailsLoadingSelector(state, subscriptionId),
    openDetails: () => openDetails(subscriptionId),
    closeDetails: () => closeDetails(subscriptionId),
    details: getDetails(state, subscriptionId),
    language: getRegionalSettings(state),
    navigateToPricingPlans,
    navigateToStores,
    cancelSubscription: () => openCancelConfirmModal(subscriptionId),
  };
};

export default connect(mapRuntimeToProps)(SubscriptionsListItem);
